import {useState} from 'react'
import {Link} from 'react-router-dom'
import {
  AlertTriangle,
  ArrowUpRight,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  UserRound,
} from 'lucide-react'
import {useApp} from '../../context/AppContext'
import type {Task} from '../../types'
import {formatDate, getDueDateState, getPriorityConfig, isTaskOverdue} from '../../utils/helpers'
import Badge from '../common/Badge'

export default function FocusQueue({tasks, boardId}: {tasks: Task[]; boardId: string}) {
  const {state, dispatch, getBoardById} = useApp()
  const [active, setActive] = useState('overdue')
  const openTasks = tasks.filter((task) => task.status !== 'done')
  const groups = [
    {
      id: 'overdue',
      label: 'Atrasadas',
      icon: AlertTriangle,
      tasks: openTasks.filter((task) => isTaskOverdue(task)),
    },
    {
      id: 'today',
      label: 'Vencem hoje',
      icon: CalendarClock,
      tasks: openTasks.filter((task) => getDueDateState(task.dueDate) === 'today'),
    },
    {
      id: 'unassigned',
      label: 'Sem responsável',
      icon: UserRound,
      tasks: openTasks.filter((task) => !task.assigneeIds.length),
    },
    {
      id: 'mine',
      label: 'Minhas pendências',
      icon: CheckCircle2,
      tasks: openTasks.filter((task) =>
        task.assigneeIds.includes(state.currentUser?.id_user || state.currentUser?.id || '')
      ),
    },
  ]
  const selected = groups.find((group) => group.id === active) || groups[0]
  const rank = {critical: 0, high: 1, medium: 2, low: 3}
  const visibleTasks = [...selected.tasks]
    .sort(
      (a, b) =>
        rank[a.priority] - rank[b.priority] ||
        (a.dueDate || '9999').localeCompare(b.dueDate || '9999')
    )
    .slice(0, 5)
  const query = new URLSearchParams({attention: active})
  if (boardId) query.set('board', boardId)

  return (
    <section className='devops-surface devops-focus' aria-label='Foco de hoje'>
      <div className='devops-focus__heading'>
        <div>
          <span className='devops-page-header__eyebrow'>Priorize sua próxima ação</span>
          <h3>Foco de hoje</h3>
          <p>Organize as pendências por prazo e responsabilidade.</p>
        </div>
        <Link className='devops-secondary-button' to={`/devops/tarefas?${query}`}>
          Ver na lista <ArrowUpRight size={15} />
        </Link>
      </div>
      <div className='devops-focus__filters' aria-label='Selecionar grupo de pendências'>
        {groups.map(({id, label, icon: Icon, tasks: groupTasks}) => (
          <button
            key={id}
            type='button'
            onClick={() => setActive(id)}
            aria-pressed={active === id}
            className={`devops-focus-filter${active === id ? ' is-active' : ''}`}
          >
            <Icon size={16} />
            <span>{label}</span>
            <strong>{groupTasks.length}</strong>
          </button>
        ))}
      </div>
      <div aria-live='polite' className='devops-focus__list'>
        {visibleTasks.length ? (
          visibleTasks.map((task) => {
            const priority = getPriorityConfig(task.priority)
            return (
              <button
                key={task.id}
                type='button'
                className='devops-focus-task'
                onClick={() => dispatch({type: 'SELECT_TASK', payload: task.id})}
              >
                <span className='devops-focus-task__main'>
                  <strong>{task.title}</strong>
                  <small>
                    {getBoardById(task.boardId)?.name || 'Quadro'} ·{' '}
                    {task.dueDate ? formatDate(task.dueDate) : 'Sem prazo definido'}
                  </small>
                </span>
                <Badge className={priority.color} dot={priority.dot}>
                  {priority.label}
                </Badge>
                <ChevronRight size={16} aria-hidden='true' />
              </button>
            )
          })
        ) : (
          <div className='devops-focus__empty'>
            <CheckCircle2 size={24} />
            <strong>Nenhuma tarefa neste grupo</strong>
            <span>Escolha outro grupo para acompanhar as demais pendências.</span>
          </div>
        )}
        {selected.tasks.length > 5 && (
          <p className='devops-focus__more'>
            Exibindo 5 de {selected.tasks.length} pendências, por prioridade. Acesse a lista para
            ver todas.
          </p>
        )}
      </div>
    </section>
  )
}
